import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const FaqMaisonJardin = ({
  setOpenIndex,
  openIndex,
  toggleFAQ,
  idCategorie,
}) => {
  const faqList = [
    {
      question: () => (
        <strong>
          1. Pourquoi utiliser Deal.mg pour acheter des articles de maison ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg offre un environnement sécurisé et une communauté active.
          Acheter de seconde main vous permet de trouver des meubles et outils
          de qualité à un prix bien inférieur au neuf.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          2. Comment vendre mes meubles ou outils de jardin rapidement ?
        </strong>
      ),
      answer: () => (
        <p>
          Prenez des photos claires de vos articles sous plusieurs angles et
          rédigez une description précise de leur état et dimensions.
          L'utilisation de notre outil d'IA aide à générer une description
          optimisée pour le <strong>SEO maison et jardin</strong>.
        </p>
      ),
    },
    {
      question: () => (
        <strong>3. Puis-je trouver des meubles artisanaux malagasy ?</strong>
      ),
      answer: () => (
        <p>
          Oui, de nombreux utilisateurs revendent des meubles en bois massif ou
          en rotin fabriqués localement. Utilisez la barre de recherche pour
          trouver des pièces uniques.
        </p>
      ),
    },
    {
      question: () => (
        <strong>4. Comment vérifier l'état d'un meuble d'occasion ?</strong>
      ),
      answer: () => (
        <p>
          Examinez la structure du meuble pour déceler des signes de faiblesse
          ou des traces de nuisibles (termites). Vérifiez le fonctionnement des
          tiroirs et des charnières.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          5. Comment fixer le juste prix pour mes articles de maison à vendre ?
        </strong>
      ),
      answer: () => (
        <p>
          Comparez vos articles avec des modèles similaires publiés sur Deal.mg,
          en tenant compte de la marque (si applicable), de l'âge et de l'état
          général.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          6. Existe-t-il des risques de contrefaçon pour les articles de
          décoration ?
        </strong>
      ),
      answer: () => (
        <p>
          Bien que rare pour les meubles, soyez vigilant sur les objets de
          marque. Pour les articles de décoration de valeur, demandez la facture
          d'origine ou vérifiez les signatures d'artistes.
        </p>
      ),
    },
    {
      question: () => (
        <strong>7. Comment signaler un article de maison suspect ?</strong>
      ),
      answer: () => (
        <p>
          Si vous détectez une annonce frauduleuse ou un article qui ne
          correspond pas à sa description, cliquez sur le bouton « Signaler »
          pour alerter notre équipe de modération.
        </p>
      ),
    },
    {
      question: () => (
        <strong>8. Puis-je vendre ou acheter des plantes sur Deal.mg ?</strong>
      ),
      answer: () => (
        <p>
          Oui, la catégorie inclut une section pour le jardinage, y compris la
          vente de plantes en pot, de graines et de terreau par des
          particuliers.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          9. Comment Deal.mg sécurise-t-il l'achat d'objets volumineux ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg facilite la mise en relation sécurisée. Pour les meubles ou
          articles de jardin volumineux, nous recommandons de{" "}
          <strong>rencontrer le vendeur pour inspecter l’objet</strong> et
          d’organiser vous-même le transport après finalisation de la
          transaction en personne.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Mobilier et jardinage à Madagascar | Deal.mg</title>
        <meta
          name="description"
          content="Découvrez notre sélection de mobilier et jardinage à Madagascar sur Deal.mg : meubles, décoration et outils de jardin à prix réduits."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: "Mobilier et jardinage à Madagascar",
            image: ["https://deal.mg/logo.png"],
            description:
              "Meubles, décoration et outils de jardin à Madagascar sur Deal.mg",
            sku: idCategorie,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/resultat?idCategorie=${idCategorie}`,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      <h1 className="text-center">
        Mobilier et jardinage : bonnes affaires locales à Madagascar
      </h1>

      <p>
        Découvrez tout le nécessaire pour aménager votre intérieur et sublimer
        votre extérieur sur Deal.mg, la plateforme de référence pour la{" "}
        <strong> maison et le jardin à Madagascar.</strong> Que vous cherchiez à
        meubler votre appartement à <strong>Antananarivo</strong> ou à équiper
        votre villa à <strong>Toamasina</strong>, trouvez des articles de
        qualité à prix réduits.
        <br />
        <br />
        En choisissant de publier votre annonce sur notre plateforme, vous
        bénéficiez d'une visibilité maximale auprès d'une communauté active,
        tout en assurant la sécurité de vos échanges.
      </p>

      <h2>Meubles et décoration d'occasion</h2>
      <p>
        Donnez une nouvelle vie à votre intérieur avec notre sélection de
        {" "}<strong>meubles occasion</strong>. Découvrez des canapés confortables,
        des tables à manger robustes, des lits et des rangements pratiques.
        Ajoutez une touche personnelle avec notre{" "}
        <strong>décoration maison</strong> : luminaires, tableaux, et objets de
        décoration uniques à Madagascar.
      </p>

      <h2>Jardinage et extérieur</h2>
      <p>
        Aménagez votre coin de verdure. Trouvez des{" "}
        <strong>outils de jardinage</strong>
        durables, des salons de jardin pour vos moments de détente, des
        barbecues pour vos repas en famille, et des plantes pour embellir votre
        espace extérieur. Le <strong>jardinage à Madagascar</strong> devient
        accessible à tous grâce aux bonnes affaires de notre communauté. C'est
        également l'endroit idéal pour trouver du{" "}
        <Link to="/divers/tous?categorie=8">
          <strong>matériel de bricolage</strong>
        </Link>{" "}
        pour vos travaux ou des appareils électroménagers dans la{" "}
        <Link to="/electronique/tous?categorie=6">
          <strong>catégorie Électronique</strong>
        </Link>{" "}
        afin de compléter votre aménagement.
      </p>

      <h2>Vendez rapidement vos meubles et déco</h2>
      <p>
        Vous déménagez ou souhaitez renouveler votre mobilier ? Publiez une
        annonce détaillée en utilisant notre outil d’IA pour optimiser votre
        description et garantir une <strong>vente rapide</strong>.
      </p>

      <br />

      <h2 className="support_faq_title">
        <MessageSquare size={20} className="support_faq_title_icon" />
        FAQ
      </h2>

      {faqList.map((item, index) => (
        <div
          key={index}
          className={`support_faq_item ${openIndex === index ? "open" : ""}`}
        >
          <div
            className="support_faq_question"
            onClick={() => toggleFAQ(index)}
          >
            <span>{item.question()}</span>
            <ChevronDown
              className={`support_faq_arrow ${
                openIndex === index ? "support_faq_rotate_180" : ""
              }`}
              size={20}
            />
          </div>

          <div
            className={`support_faq_answer_container${
              openIndex === index ? "active" : ""
            }`}
          >
            <div className="support_faq_answer">{item.answer()}</div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FaqMaisonJardin;
